import { MenuItem, Select, SelectChangeEvent } from "@mui/material"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import React, { ReactNode } from "react"
import styled from "styled-components"
import { FastBackward, FastForward } from "../../../../icons"
import { theme } from "../../../../theme"
import { ButtonText } from "../../../atoms/button"
import { TextRegular } from "../../../atoms/text"
import { PaginationConfig } from "../types/config/PaginationConfig"

export type PaginationProps = PaginationConfig & {
  rowStart: number
  setRowStart: (newRowStart: number) => void
  rowEnd: number
  setRowEnd: (newRowEnd: number) => void
  rowsPerPage: number
  setRowsPerPage: (newRowsPerPage: number) => void
  onChange: (newRowStart?: number, newRowEnd?: number) => void
}

export const Pagination = (props: PaginationProps) => {
  const {
    rowsPerPageText,
    rowsPerPageOptions,
    totalRows,
    pageXofYText,
    rowStart,
    setRowStart,
    rowEnd,
    setRowEnd,
    rowsPerPage,
    setRowsPerPage,
    onChange,
  } = props

  const handleChangeRowsPerPage = (event: SelectChangeEvent<unknown>, child: ReactNode) => {
    const newRowsPerPage = event.target.value as number
    setRowsPerPage(newRowsPerPage)
    const theoreticalRowEnd = rowStart + newRowsPerPage - 1
    const newRowEnd = totalRows ? Math.min(theoreticalRowEnd, totalRows) : theoreticalRowEnd
    const newRowStart =
      totalRows && theoreticalRowEnd >= totalRows ? Math.max(totalRows - newRowsPerPage + 1, 1) : rowStart

    setRowStart(newRowStart)
    setRowEnd(newRowEnd)

    if (rowEnd !== newRowEnd || rowStart !== newRowStart) {
      onChange(newRowStart, newRowEnd)
    }
  }

  const handleFastBackward = () => {
    setRowStart(1)
    setRowEnd(rowsPerPage)
    onChange(1, rowsPerPage)
  }

  const handleBack = () => {
    const newRowStart = Math.max(1, rowStart - rowsPerPage)
    const newRowEnd = Math.max(rowsPerPage, rowEnd - rowsPerPage)

    setRowStart(newRowStart)
    setRowEnd(newRowEnd)
    onChange(newRowStart, newRowEnd)
  }

  const handleForward = () => {
    const newRowStart = totalRows
      ? Math.min(rowStart + rowsPerPage, totalRows - rowsPerPage + 1)
      : rowStart + rowsPerPage
    const newRowEnd = totalRows ? Math.min(rowEnd + rowsPerPage, totalRows) : rowEnd + rowsPerPage

    setRowStart(newRowStart)
    setRowEnd(newRowEnd)

    onChange(newRowStart, newRowEnd)
  }

  const handleFastForward = () => {
    if (!totalRows) {
      return
    }

    const newRowStart = Math.max(totalRows - rowsPerPage + 1, 1)
    const newRowEnd = totalRows

    setRowStart(newRowStart)
    setRowEnd(newRowEnd)

    onChange(newRowStart, newRowEnd)
  }

  return (
    <UnderTableActions>
      <PaginationDiv>
        <RowsPerPageSelectDiv>
          {rowsPerPageText}
          <CustomSelect
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            disableUnderline
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
            }}
          >
            {rowsPerPageOptions.map((option: number, index) => {
              return (
                <MenuItem value={option} key={`option-${index}`}>
                  {option}
                </MenuItem>
              )
            })}
          </CustomSelect>
          {totalRows ? (
            <TextRegular>{`${rowStart}-${rowEnd} ${pageXofYText} ${totalRows}`}</TextRegular>
          ) : (
            <TextRegular>{`${rowStart}-${rowEnd}`}</TextRegular>
          )}
          <PageNavigationDiv>
            <PageNavigationButton
              disabled={rowStart === 1}
              onClick={handleFastBackward}
              icon={<FastBackward />}
              noBackground={true}
            ></PageNavigationButton>
            <PageNavigationButton
              disabled={rowStart === 1}
              onClick={handleBack}
              icon={<ChevronLeft />}
              noBackground={true}
            ></PageNavigationButton>
            <PageNavigationButton
              disabled={totalRows ? rowEnd === totalRows : false}
              onClick={handleForward}
              icon={<ChevronRight />}
              noBackground={true}
            ></PageNavigationButton>
            <PageNavigationButton
              disabled={totalRows ? rowEnd === totalRows : true}
              onClick={handleFastForward}
              icon={<FastForward />}
              noBackground={true}
            ></PageNavigationButton>
          </PageNavigationDiv>
        </RowsPerPageSelectDiv>
      </PaginationDiv>
    </UnderTableActions>
  )
}

const CustomSelect = styled(Select)`
  border: none;
  background-color: transparent;

  & .MuiSelect-select {
    background-color: unset;
  }

  svg {
    color: ${theme.color.black6};
  }
`

const UnderTableActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: ${theme.spacing.lx} ${theme.spacing.m};
`

const PaginationDiv = styled.div`
  display: flex;
  gap: ${theme.spacing.xl};
  align-items: center;
`

const RowsPerPageSelectDiv = styled.div`
  display: flex;
  gap: ${theme.spacing.lx};
  align-items: center;
`

const PageNavigationDiv = styled.div`
  display: flex;
  gap: ${theme.spacing.m};
  align-items: center;
`

const PageNavigationButton = styled(ButtonText)`
  min-width: ${theme.spacing.lx};
  max-width: ${theme.spacing.lx};
  max-height: ${theme.spacing.lx};
  min-height: ${theme.spacing.lx};
  padding: 0;

  svg {
    width: 24px;
    height: 24px;
    color: ${theme.color.primary};
    fill: ${theme.color.primary};
  }

  &&:disabled {
    svg {
      color: ${theme.color.grey2};
      fill: ${theme.color.grey2};
    }
    background-color: transparent !important;
  }
`
